import { HostName } from "pages/About/hosts";
import { Image } from "shared/types";

export type CategoryTag = string | string[]

export type EpisodeType = 'regular' | 'bonus' | 'side-quest'

export interface EpisodeResource {
  label: string;
  url?: string;
}

export interface EpisodeResourceGroup {
  tag: string;
  resources: EpisodeResource[]
}

export interface DiscussionPoint {
  title: string;
  description: string;
}

export interface ShowNotes {
  intro: string;
  quote?: {
    text: string;
    attribution: string;
  };
  discussionPoints: DiscussionPoint[];
}

export interface SyndicatedEpisode {
  buzzsproutId: number;
  episodeNumber?: number;
  title: string;
  description: string;
  publishedDate: Date;
  audioUrl: string;
  durationSeconds: number;
  artworkUrl?: string;
}

export interface EpisodeEntry {
  number: string;
  buzzsproutId?: number;
  title: string;
  description: string;
  image: Image;
  titleHighlight?: string;
  type?: EpisodeType;
  host?: HostName;
  category?: CategoryTag;
  showNotes?: ShowNotes;
  resources?: EpisodeResourceGroup[];
  artworkUrl?: string;
}

export interface PublishedEpisode extends EpisodeEntry {
  buzzsproutId: number;
  type: EpisodeType;
  host: HostName;
  category: CategoryTag;
  showNotes: ShowNotes;
  publishedDate: Date;
  audioUrl: string;
  durationSeconds: number;
  durationTimestamp: string;
}

export interface UpcomingEpisode extends EpisodeEntry {
  publishedDate?: never;
  audioUrl?: never;
}

export type Episode = UpcomingEpisode | PublishedEpisode;

export const isUpcomingEpisode = (episode: Episode): episode is UpcomingEpisode =>
  episode.buzzsproutId === undefined;

export const isPublishedEpisode = (episode: Episode): episode is PublishedEpisode =>
  episode.buzzsproutId !== undefined;

export const EPISODES: EpisodeEntry[] = [
  {
    number: '6',
    buzzsproutId: 20052483,
    host: 'Liz',
    category: ['Folklore', 'Mysteries'],
    image: {
      src: '/images/episodes/6-seasonal-tales-for-a-fall-night.png',
      alt: 'A fluffy black cat with amber eyes sits beside a glowing carved pumpkin and turnip and an open illustrated manuscript, under a full moon and a castle on a misty lake',
    },
    title: 'Seasonal Tales for a Fall Night',
    titleHighlight: 'Fall Night',
    description:
      "A trickster, a fairy cat and a book nobody can read. Grab a blanket, because the moms are telling Hallowe'en stories!",
    showNotes: {
      intro:
        "It's Hallowe'en night, so dim the lights and pull up a chair. We have three stories for you: a lantern born from a deal with the Devil, a cat that may be more than a cat, and a book written in a language no one can read. Then we talk about the scariest thing of all: the daycare sniffles that never seem to end.",
      discussionPoints: [
        {
          title: "Stingy Jack and the Jack-o'-Lantern",
          description:
            'How an Irish tale of a man who tricked the Devil gave us carved lanterns, and why turnips came long before pumpkins.',
        },
        {
          title: 'Cat Nights and the Cait Sìth',
          description:
            'Two separate traditions that make a purrfect pair: the August Cat Nights, when witches were said to take feline form, and the spectral fairy cat of Scottish Highland folklore.',
        },
        {
          title: 'The Voynich Manuscript',
          description:
            'A fifteenth-century book filled with an unknown script and strange illustrations, held at Yale and still undeciphered, with theories ranging from scholarly to wild.',
        },
        {
          title: 'Mom Chats: The Daycare Diseases',
          description:
            'Daycare brings friends, routines and every germ in town. We swap stories about the endless colds that come home with our little ones.',
        },
      ],
    },
    resources: [
      {
        tag: 'Read',
        resources: [
          {
            label: 'The Origins of Halloween Traditions',
            url: 'https://blogs.loc.gov/headlinesandheroes/2021/10/the-origins-of-halloween-traditions/',
          },
          {
            label: "The Twisted Transatlantic Tale of American Jack-o'-Lanterns",
            url: 'https://www.nationalgeographic.com/history/article/the-twisted-transatlantic-tale-of-american-jack-o-lanterns',
          },
          {
            label: 'When People Carved Turnips Instead of Pumpkins for Halloween',
            url: 'https://www.smithsonianmag.com/smart-news/when-people-carved-turnips-instead-of-pumpkins-for-halloween-180978922/',
          },
          {
            label: "How Jack O'Lanterns Originated in Irish Myth",
            url: 'https://www.history.com/articles/history-of-the-jack-o-lantern-irish-origins',
          },
          {
            label: 'Superstitions of the Highlands and Islands of Scotland',
            url: 'https://www.gutenberg.org/ebooks/61730',
          },
          {
            label: 'Popular Tales of the West Highlands',
            url: 'https://www.gutenberg.org/ebooks/71239',
          },
          {
            label: 'The Beinecke Cipher (Voynich) Manuscript',
            url: 'https://beinecke.library.yale.edu/beinecke/collections/beinecke-cipher-voynich-manuscript',
          },
          {
            label: 'Voynich Manuscript',
            url: 'https://search.library.yale.edu/archives/11167038',
          },
          {
            label: 'Radiocarbon Dating of the Voynich MS',
            url: 'https://voynich.nu/extra/carbon.html',
          },
          {
            label: 'The Voynich Manuscript',
            url: 'https://www.medievalists.net/2021/08/the-voynich-manuscript/',
          },
          {
            label: 'Voynich Manuscript Questions',
            url: 'https://elenigmavoynich.com/en/voynich-questions',
          },
          {
            label: 'Fall Equinox & The Voynich Manuscript',
            url: 'https://allymaz.substack.com/p/fall-equinox-the-voynich-manuscript',
          },
        ],
      },
    ],
  },
  {
    number: '5',
    buzzsproutId: 20018716,
    host: 'Kelly',
    category: ['Folklore', 'Legends'],
    image: {
      src: '/images/episodes/5-mishipeshu.png',
      alt: 'An antlered, scaled creature with glowing amber eyes rises from a dark lake under a starry sky beside a wooden rowboat',
    },
    title: 'Mishipeshu!',
    titleHighlight: 'Mishipeshu!',
    description:
      "We're treading water as we explore an Indigenous tale of a being beneath the Great Lakes. It's the Mishipeshu!",
    showNotes: {
      intro:
        'Horns, scales, a long whirlpool-spinning tail, and eyes glowing beneath the waves. Let\'s meet the Mishipeshu, the underwater guardian of the Great Lakes, and creature of many names. After, we\'ll discuss how to outsmart toddlers who have gained skills we\'re not ready for.',
      quote: {
        text: "But, you know, don't piss it off or else there's gonna be an Edmund Fitzgerald song written about you.",
        attribution: 'Liz',
      },
      discussionPoints: [
        {
          title: 'The Underwater Panther',
          description:
            'Spelled more than 40 ways and often translated as "underwater panther" or "great lynx," the Mishipeshu has a feline body with horns, scales, spines and a long tail that stirs up whirlpools between worlds.',
        },
        {
          title: "Don't Say Its Name",
          description:
            'In some communities the name isn\'t spoken, because saying it can call the being forth. Throwing caution to the wind we discuss the beliefs surrounding its power.',
        },
        {
          title: 'Mishipeshu and the Thunderbird',
          description:
            'One rules the sky and one lives beneath the water. The two are in constant conflict and constant relation, and storms brew where they meet.',
        },
        {
          title: 'Respect the Water',
          description:
            "The Mishipeshu isn't treated as an old legend but as a spirit still present in the lakes, one to respect just like the water itself.",
        },
        {
          title: 'Mom Chats: The Escape Artists',
          description:
            'Doors get opened and then slammed shut. A toddler demonstrates that no door can hold her back, and some toys seem designed to encourage escape.',
        },
      ],
    },
  },
  {
    number: 'side-quest-2',
    buzzsproutId: 19832257,
    host: 'Kelly',
    type: 'side-quest',
    category: ['Mythology', 'Sidequest'],
    image: {
      src: '/images/episodes/side-quest-2-the-story-of-change.png',
      position: 'top',
      alt: "Chang'e in flowing blue and violet silk robes stands on the moon's surface beside a blossoming tree under a starry sky",
    },
    title: "The Story of Chang'e",
    titleHighlight: "Chang'e",
    description:
      "AJ and Liz are slaying beasts at bedtime so Kelly takes the reins for the second ever Sidequest episode. Today she will go into the legend of Chang'e, the story of her and Hou Yi, the Jade Rabbit and some pop culture influences.",
    showNotes: {
      intro:
        "Would you want to be separated from your partner? Uh, no one does. But that's what happens in the Chinese myth of Chang'e and her husband Hou Yi. Kelly goes into detail for her first ever side quest episode into the multiple versions of this story, the Jade Rabbit, how the story of Chang'e has influenced culture and pop culture references.",
      quote: {
        text: "Like so many myths, the story changes depending on who's telling it.",
        attribution: 'Kelly',
      },
      discussionPoints: [
        {
          title: 'Ten Suns and a Legendary Archer',
          description:
            'When ten suns scorched the earth, Hou Yi shot down nine of them, leaving the one we have today, and was rewarded with an elixir of immortality.',
        },
        {
          title: 'The Elixir and the Flight to the Moon',
          description:
            "Did Chang'e drink the elixir to keep it from a thief, or take it for herself? The story shifts depending on who tells it, and so does Hou Yi's fate.",
        },
        {
          title: 'The Jade Emperor and the Jade Rabbit',
          description:
            'In some versions the Jade Emperor lets Hou Yi visit his wife, while the Jade Rabbit keeps her company on the moon, pounding the elixir of life.',
        },
        {
          title: 'The Mid-Autumn Festival',
          description:
            "Every year in late September or early October, Chang'e is honoured with incense, prayers and a celebration of the moon.",
        },
        {
          title: "Chang'e in Pop Culture",
          description:
            "From China's Chang'e lunar probes to Sailor Moon's moon princess Usagi and Netflix's Over the Moon, her story keeps inspiring new ones.",
        },
      ],
    },
    resources: [
      {
        tag: 'Listen',
        resources: [
          {
            label: 'Bedtime Stories (Growing Up Baby)',
            url: 'https://growingupbaby.buzzsprout.com/1946166/episodes/10705294-bedtime-stories',
          },
        ],
      },
      {
        tag: 'Read',
        resources: [
          {
            label: "Chang'e (deity)",
            url: 'https://www.ebsco.com/research-starters/social-sciences-and-humanities/change-deity',
          },
          {
            label: "Chang'e",
            url: 'https://en.wikipedia.org/wiki/Chang%27e',
          },
          {
            label: 'Moon rabbit',
            url: 'https://en.wikipedia.org/wiki/Moon_rabbit',
          },
          {
            label: 'Myths of China by Xiaobing Wang',
            url: 'https://www.goodreads.com/book/show/200555226-myths-of-china',
          },
        ],
      },
    ],
  },
  {
    number: '4',
    buzzsproutId: 19727775,
    host: 'Kelly',
    category: ['Folklore', 'Legends'],
    image: {
      src: '/images/episodes/4-lakes-legends-and-lore-continued.png',
      alt: 'Ghostly figures gather around a sunken shipwreck on the lakebed beneath a stormy, moonlit sky',
    },
    title: 'Lakes, Legends and Lore Continued!',
    titleHighlight: 'Continued!',
    description:
      'We continue our Great Lakes deep dive, meet the personalities of each lake, and chat about finding ourselves again past the newborn stage.',
    showNotes: {
      intro:
        "The podcast has awoken again! We pick up where we left off with the Great Lakes, after a few (okay, several) detours. Kelly shares how people talk about the lakes as if they each have a personality, the chilling science behind one of the most famous sayings about Lake Superior, and a strange sighting of her own. Then the moms talk hobbies: losing them, finding them again, and the guilt that sometimes tags along.",
      quote: {
        text: "... You rediscover yourself, but at the same time, you haven't lost yourself, you're just redefining.",
        attribution: 'Liz',
      },
      discussionPoints: [
        {
          title: 'Tornado Tangents',
          description:
            'The lakes have an intense effect on weather, we talk about the storm stories from we\'ve heard and experienced.',
        },
        {
          title: 'The Personalities of the Great Lakes',
          description:
            'Huron has integrity, Ontario is reliable, Michigan has many faces, Erie is temperamental, and Superior is the matriarch.',
        },
        {
          title: 'Superior Never Gives Up Her Dead',
          description:
            'The line made famous by Gordon Lightfoot has a chilling explanation rooted in how cold Lake Superior really is.',
        },
        {
          title: 'Mom Chats: Hobbies After Baby',
          description:
            'How our hobbies disappeared, how they are coming back, and why making time for yourself matters.',
        },
      ],
    },
    resources: [
      {
        tag: 'Read',
        resources: [
          {
            label: 'The Wreck of the Edmund Fitzgerald',
            url: 'https://en.wikipedia.org/wiki/The_Wreck_of_the_Edmund_Fitzgerald',
          },
          {
            label: 'Great Lakes Influence Weather and Climate',
            url: 'https://www.eekwi.org/great-lakes/weather-climate-and-great-lakes/great-lakes-influence-weather-and-climate',
          },
          {
            label: 'Lake Huron: The Lake of Integrity',
            url: 'https://www.facebook.com/story.php?story_fbid=1452462112907839&id=100044321925123',
          },
          {
            label: 'Lake Ontario: The Reliable Lake',
            url: 'https://www.facebook.com/share/1DCjZxfwf6/',
          },
          {
            label: 'Lake Michigan: A Lake with Many Faces',
            url: 'https://www.facebook.com/share/1CYRFcUb42/',
          },
          {
            label: 'Lake Erie: The Temperamental Lake',
            url: 'https://www.facebook.com/share/1DApffLXz6/',
          },
          {
            label: 'Lake Superior: The Patriarch/Matriarch',
            url: 'https://www.facebook.com/share/1ByAYsYhLv/',
          },
        ],
      },
    ],
  },
  {
    number: '3',
    buzzsproutId: 19663154,
    host: 'Kelly',
    category: ['Folklore', 'Legends'],
    image: {
      src: '/images/episodes/3-lakes-legends-and-lore.png',
      alt: 'A ghostly ship glows on stormy waves beneath a lighthouse on a rocky cliff',
    },
    title: 'Lakes, Legends and Lore',
    titleHighlight: 'Lakes',
    description:
      "The Great Lakes are... well, great. Before we dive into their myths and legends, we get to know the lakes themselves.",
    showNotes: {
      intro:
        "The Great Lakes are enormous freshwater seas with shipwrecks, strange weather, ghost stories, sea monsters, and more than a little personality. There are so many legends surrounding them to dig into, but first we need to get to know the lakes themselves.",
      quote: {
        text: "I think we don't let him name things anymore.",
        attribution: 'AJ',
      },
      discussionPoints: [
        {
          title: 'Mom Chats: Naps, Names and "Why?"',
          description:
            'Dropped naps, going from "mama" to "mommy", a toddler asking why about everything, and Liz\'s trick for stubborn mornings.',
        },
        {
          title: 'Getting to Know the Great Lakes',
          description:
            'HOMES: Huron, Ontario, Michigan, Erie and Superior. Together they could almost fit the entire United Kingdom, and Superior\'s underwater mountain.',
        },
        {
          title: 'Weird Weather on the Lakes',
          description:
            'Seiches, waterspouts, steam devils, rogue waves, the "three sisters", and the theoretical rogue hole.',
        },
        {
          title: 'The Great Lakes Triangles, and Graveyards',
          description:
            'Lake Michigan has its own Bermuda Triangle, and Lake Ontario has the Marysburgh Vortex. An estimated 6,000 to 10,000 shipwrecks lie beneath the lakes.',
        },
      ],
    },
    resources: [
      {
        tag: 'Read',
        resources: [
          {
            label: 'The Great Lakes by Depth',
            url: 'https://www.worldatlas.com/lakes/the-great-lakes-by-depth.html',
          },
          {
            label: 'Great Lakes Fast Facts',
            url: 'https://www.michiganseagrant.org/topics/great-lakes-fast-facts/',
          },
          {
            label: 'Superior Shoal Under the Microscope',
            url: 'https://www.cbc.ca/news/canada/thunder-bay/superior-shoal-thunder-bay-9.6933006',
          },
          {
            label: "Eerie Photos Show 'Steam Devils' Rising Over Lake Ontario",
            url: 'https://canadiangeographic.ca/articles/eerie-photos-show-steam-devils-rising-over-lake-ontario/',
          },
          {
            label: 'Scientists Confirm Lake Superior Rogue Waves',
            url: 'https://www.duluthnewstribune.com/news/scientists-confirm-lake-superior-rogue-waves',
          },
          {
            label: 'The Bermuda Triangle of the Great Lakes',
            url: 'https://greatlakesmuseum.ca/the-bermuda-triangle-of-the-great-lakes/',
          },
        ],
      },
    ],
  },
  {
    number: 'side-quest-1',
    buzzsproutId: 19381588,
    host: 'Liz',
    type: 'side-quest',
    category: ['Folklore', 'Sidequest'],
    image: {
      src: '/images/episodes/side-quest-1-sips-solstice-and-shakespeare.png',
      alt: '',
    },
    title: 'Sips, Solstice and Shakespeare',
    titleHighlight: 'Shakespeare',
    description:
      "Our inaugural Side Quest episode is out. Liz takes the reigns for a quick episode about summer solstice and folklore influences on characters in Shakespeare's Midsummer Nights Dream",
    showNotes: {
      intro:
        "Welcome to our very first Side Quest! These shorter, solo-host adventures are how we keep the campaign rolling when the full party can't gather. With the Summer Solstice on the way, Liz heads off on her own to dig into the mythological and folkloric roots of one of Shakespeare's most beloved comedies: A Midsummer Night's Dream. Grab a drink (Liz is sipping a very glamorous Coca-Cola) and join her in the forest.",
      quote: {
        text: "...you've probably met a toddler with strong Puck energy.",
        attribution: 'Liz',
      },
      discussionPoints: [
        {
          title: 'Midsummer and the Veil Between Worlds',
          description:
            'A liminal night when the veil between worlds grew thin, fairies roamed freely, and young people performed rituals to glimpse their future spouses.',
        },
        {
          title: 'Theseus and Hippolyta: Greek Mythology on Stage',
          description:
            "The play's human rulers come straight from Greek myth: Theseus, slayer of the Minotaur, and Hippolyta, Queen of the Amazons.",
        },
        {
          title: "Oberon and Titania's Custody Battle",
          description:
            'The fairy king and queen squabble over a young boy. Naturally, Oberon resolves things with enchanted flower juice and a donkey-headed suitor.',
        },
        {
          title: 'The Four Lovers and the Enchanted Flower',
          description:
            'Hermia, Lysander, Demetrius, and Helena tangled in a flowchart of mismatched affection, with magical plants from European folklore making it worse.',
        },
        {
          title: 'Puck, the Patron Saint of Chaos',
          description:
            "An English folklore hobgoblin who sometimes helps, sometimes causes trouble, usually both. He drives the play's comedy and gets the final word.",
        },
      ],
    },
  },
  {
    number: '2',
    host: "Liz",
    category: ["Folklore", "Mythology"],
    image: {
      src: '/images/episodes/2-not-the-bees.jpg',
      alt: 'A swarm of bees on a galactic balcony.'
    },
    title: "Not the Bees!",
    titleHighlight: "Bees!",
    description:
      "Bees are bee-u-tiful, wonderful and important insects. But did you know that there’s a lot more to them from a myth stand point?",
    showNotes: {
      intro:
        "The nicer weather is upon us and with that comes out the creepy crawlies. For today Liz takes over and talks about one of the most essential bugs for our world: The Bees.  Bees are absolutely bee-utiful but what lore is there about them? We talk about why the bee has its sting, some urban legends for beekeeping, and how we can help out colonies. Plus, the moms talk about recent milestones and sleep struggles.",
      quote: {
        text: "Bees? Bees? BEES?!",
        attribution: "AJ",
      },
      discussionPoints: [
        {
          title: "Bee Basics",
          description:
            "A quick primer on what bees actually are, the social structure of a hive, and a surprising defense mechanism.",
        },
        {
          title: "Telling It to the Bees",
          description:
            "The old tradition of treating a hive like family. Beekeeper etiquette, and the close bond between beekeepers and their bees.",
        },
        {
          title: "Mythology of Bees",
          description:
            "From a Bee's bargain with Zeus, to the Egyptians using honey to embalm their dead, and the Celtic laws so strict that stealing a hive was a capital offence. Bees have been revered across millennia.",
        },
        {
          title: "How to Help the Bees",
          description:
            "Practical things you can do to be more bee-friendly. From \"bee baths\" to buying local.",
        },
        {
          title: "Mom Chats: All Things Sleep",
          description:
            "Our own sleep (or lack of it), our kids' sleep struggles and milestones, and the funny anecdotes we couldn't leave out.",
        },
      ],
    },
    resources: [
      {
        tag: 'Read',
        resources: [
          {
            label: 'Honey Bee Legends, Myths, and Folklore',
            url: 'https://www.farmersalmanac.com/honey-bee-legends',
          },
          {
            label: 'Why Bees Have Become Cultural Icons: From Mythology to Modern Trends',
            url: 'https://hivekeepers.com/blogs/news/why-bees-have-become-cultural-icons-from-mythology-to-modern-trends',
          },
          {
            label: 'Bee Folklore for Dark Days',
            url: 'https://www.beepods.com/bee-folklore-for-dark-days/',
          },
          {
            label: 'Mythos by Stephen Fry',
            url: "https://www.goodreads.com/book/show/35074096-mythos"
          },
        ],
      },
    ],
  },
  {
    number: 'easter-bonus',
    host: 'AJ',
    type: 'bonus',
    category: ['Bonus', 'Folklore'],
    image: {
      src: '/images/episodes/easter-bonus-on-the-hunt-for-the-easter-bunny.png',
      alt: 'A bunny sits in a magical forest surrounded by eggs that are covered in cryptic text'
    },
    title: 'On the Hunt for the Easter Bunny',
    titleHighlight: 'Easter Bunny',
    description:
      'What do bunnies and eggs have to do with Easter? Let\'s explore some Easter traditions and their origins',
    showNotes: {
      intro:
        'Is Easter actually named for the goddess Ishtar? What do bunnies and eggs have to do with it? Let\'s dig into the murky origins of the holiday\'s name, question its pagan roots, and trace how a rabbit hiding eggs somehow became an iconic tradition. All while sharing some of our own favourite childhood Easter memories, and our plans for this year.',
      quote: {
        text: 'It\'s also a good reminder to not blindly believe everything you read on the internet.',
        attribution: 'Kelly',
      },
      discussionPoints: [
        {
          title: 'Where did Easter get its name?',
          description:
            'Online claims of pagan origins attributed to multiple different goddesses, but which one is true?',
        },
        {
          title: "The Easter bunny and its colourful eggs",
          description:
            'What does a bunny have to do with Easter? Why do we give children colourful eggs and chocolate?',
        },
        {
          title: "The Easter... Bilby?",
          description:
            'Australia has chosen to eschew the Easter Bunny in favour of a more local wildlife icon'
        },
        {
          title: 'Easter plans and childhood memories',
          description:
            'We talk about how we\'re making Easter special for our children, while reminiscing about what we loved from our own childhoods.',
        },
      ],
    },
    resources: [
      {
        tag: 'Read',
        resources: [
          {
            label: 'Easter, Ishtar, Eoster and Eggs',
            url: 'https://historyforatheists.com/2017/04/easter-ishtar-eostre-and-eggs/'
          },
          {
            label: 'Eosturmonath (April)',
            url: 'https://www.carlanayland.org/essays/eosturmonath.htm'
          },
          {
            label: 'On the Bunny Trail: In Search of the Easter Bunny',
            url: 'https://blogs.loc.gov/folklife/2016/03/easter-bunny/'
          },
          {
            label: 'Easter Bilby',
            url: 'https://en.wikipedia.org/wiki/Easter_Bilby'
          },
        ]
      }
    ],
  },
  {
    number: '1',
    buzzsproutId: 19035086,
    host: 'Liz',
    category: 'Cryptids',
    image: {
      src: '/images/episodes/1-lets-get-kraken.png',
      alt: 'The Kraken has emerged from the watery depths of the ocean clutching a chest'
    },
    title: 'Let\'s Get Kraken',
    titleHighlight: 'Kraken',
    description:
      'In our inaugural episode, our trio of hero mamas discuss the most legendary monster of the seas, the Kraken.',
    showNotes: {
      intro:
        'What lurks beneath the waves? In our very first episode, join us while we dive into the origins of the Kraken. From twisted Scandinavian folklore to dying giant squids flailing at the surface. We explore how Norse sailors turned real ocean encounters into terrifying legends, and what modern marine biology has to say about it all. After get to know the hosts a bit while we discuss the origin of our own motherhood stories, pregnancy!',
      quote: {
        text: 'Whales are sea monsters.',
        attribution: 'Kelly',
      },
      discussionPoints: [
        {
          title: 'Origins of the Kraken',
          description:
            'From the Scandinavian word "krake" meaning twisted or sickly, to Norse legends of a colossal squid-like beast that could drag entire ships to the ocean floor.',
        },
        {
          title: 'The Giant Squid Theory',
          description:
            'Marine biologist in.vertebabe explains how dying giant squids fill with ammonia, rise to the surface in a delirious state, and flail into ships creating instant Kraken sightings.',
        },
        {
          title: 'Fishermen\'s Friend or Foe?',
          description:
            'Not every Kraken encounter was a disaster, schools of fish would rise to the surface in its wake, giving brave fishermen a massive catch if they survived.',
        },
        {
          title: 'Pregnancy Stories',
          description:
            'Three very different journeys into motherhood: meticulously planned, "if it happens, it happens," and a complete surprise. Plus, how we each broke the news to our partners.',
        },
      ],
    },
    // resources: [
    //   {
    //     tag: 'Watch',
    //     resources: [
    //       { label: 'in.vertebabe on TikTok: Giant Squid and the Kraken' },
    //     ]
    //   },
    //   {
    //     tag: 'Read',
    //     resources: [
    //       { label: 'The Kraken in Scandinavian Folklore' },
    //       { label: 'Scylla and Charybdis in Greek Mythology' },
    //     ]
    //   },
    // ],
  },
];
